/**
 * Group conversations: the row, the header, the roster, and making one.
 *
 * A group is a DM with a name and more than two people in it, so it reuses
 * the chat log and the composer wholesale. What is different is who is in it
 * and who may change that, and all of it lives here rather than being spread
 * through the chat screen.
 */

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { Group, GroupMember, PublicUser } from '@/lib/types';
import { useSession } from '@/stores/session';
import { Avatar } from './Avatar';
import { UserName, openUserCard } from './user';
import { Button, IconButton, Modal, useConfirm, useToast } from './ui';
import { useContextMenu, type MenuItem } from './Menu';
import { ChatTools } from './ChatTools';
import { Icon } from '@/icons/Icon';
import { CallButton, groupRoom } from './ChatCall';

/* ------------------------------------------------------------- the picture */

/**
 * A group has no avatar of its own, so it borrows its members'.
 *
 * Up to three, overlapped. A generated monogram would need a colour scheme
 * nobody chose; the faces are already there and they say who is in it, which
 * is the thing you are actually scanning for.
 */
export function GroupAvatar({ group, size = 34 }: { group: Group; size?: number }) {
  const faces = group.members.slice(0, 3);
  const each = Math.round(size * 0.62);

  return (
    <span
      className="relative flex-none"
      style={{ width: size, height: size }}
      aria-label={group.name}
    >
      {faces.map((m, i) => (
        <span
          key={m.userId}
          className="absolute overflow-hidden rounded-full ring-2 ring-[var(--bg-1)]"
          style={{
            width: each,
            height: each,
            left: i * Math.round((size - each) / Math.max(1, faces.length - 1 || 1)),
            top: i % 2 ? size - each : 0,
            zIndex: faces.length - i
          }}
        >
          <Avatar user={m.user} size={each} />
        </span>
      ))}
      {group.memberCount > 3 ? (
        <span className="absolute right-0 bottom-0 grid h-[15px] min-w-[15px] place-items-center rounded-full bg-surface-3 px-0.5 text-[8.5px] font-extrabold text-dim ring-2 ring-[var(--bg-1)]">
          +{group.memberCount - 3}
        </span>
      ) : null}
    </span>
  );
}

/* ----------------------------------------------------------------- the row */

/** A group in the conversation list, shaped like the DM rows beside it. */
export function GroupRow({ group, active, onOpen, onLeave }: {
  group: Group;
  active: boolean;
  onOpen: () => void;
  onLeave: () => void;
}) {
  const me = useSession((s) => s.me);
  const ctx = useContextMenu(() => [
    { label: 'Open', icon: 'chat', onClick: onOpen },
    { label: 'Leave group', icon: 'close', danger: true, onClick: onLeave }
  ]);

  const last = group.last;
  const who = last?.userId === me?.id ? 'You' : last?.user?.displayName;
  const preview = last
    ? (last.kind === 'system' ? last.body : (who ? who + ': ' : '') + (last.body || 'an attachment'))
    : String(group.memberCount) + ' people';

  return (
    /*
     * A button, like every other row in this column.
     *
     * It was a div, and clicking it did nothing at all - the announcements
     * row and the direct-message rows beside it are buttons and worked fine.
     * A row you can click is a control, so it should be the element the
     * platform already knows how to click, focus and reach by keyboard.
     */
    <button
      type="button"
      {...ctx.props}
      onClick={onOpen}
      className={cn(
        'group flex w-full cursor-pointer items-center gap-3.5 rounded-[calc(16px*var(--radius-mul,1))] px-3 py-3 text-left outline-none transition-[background-color,box-shadow] duration-[250ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        active ? 'bg-accent-soft shadow-[inset_0_0_0_1.5px_color-mix(in_srgb,var(--accent)_80%,transparent)]' : 'hover:bg-surface-2 hover:shadow-[inset_0_0_0_1px_var(--line-soft)]'
      )}
    >
      <GroupAvatar group={group} size={44} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[14.5px] font-bold">{group.name}</span>
          <Icon name="users" size={11} className="flex-none text-ghost" />
        </span>
        <span className="block truncate text-[11.5px] text-mute">{preview}</span>
      </span>
      {group.unread ? (
        <span className="grid h-[17px] min-w-[17px] flex-none place-items-center rounded-full bg-accent px-1 text-[10px] font-extrabold text-accent-fg">
          {group.unread > 99 ? '99+' : group.unread}
        </span>
      ) : null}
      {ctx.menu}
    </button>
  );
}

/* -------------------------------------------------------------- the roster */

/**
 * Who is in it, and what each person may do.
 *
 * Roles are shown on the row rather than hidden in a menu, because the whole
 * question the roster answers is "who can kick me out of this".
 */
export function GroupPanel({ group, onLeave }: { group: Group; onLeave: () => void }) {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const myRole = group.myRole;
  const isOwner = myRole === 'owner';
  const isAdmin = isOwner || myRole === 'admin';
  const mayInvite = isAdmin || group.openInvites;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['group', group.id] });
    void queryClient.invalidateQueries({ queryKey: ['groups'] });
  }

  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: refresh,
    onError: (err: Error) => toast(err.message, 'bad')
  });

  /* Owner first, then admins, then everyone else by when they joined. */
  const ordered = useMemo(() => {
    const rank = { owner: 0, admin: 1, member: 2 } as const;
    return [...group.members].sort((a, b) =>
      (rank[a.role] - rank[b.role]) || (a.joinedAt - b.joinedAt));
  }, [group.members]);

  function menuFor(m: GroupMember, anchor: () => HTMLElement | null): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: 'View profile',
        icon: 'user',
        // The card is placed against the row it came from, so it opens where
        // you were looking rather than in the middle of the screen.
        onClick: () => { const el = anchor(); if (el) openUserCard(m.user, el); }
      }
    ];
    if (m.userId === me?.id) return items;

    if (isOwner && m.role !== 'owner') {
      items.push({
        label: m.role === 'admin' ? 'Remove admin' : 'Make admin',
        icon: 'shieldNav',
        onClick: () => act.mutate(() =>
          endpoints.setGroupRole(group.id, m.userId, m.role === 'admin' ? 'member' : 'admin'))
      });
      items.push({
        label: 'Hand over the group',
        icon: 'crown',
        onClick: async () => {
          const ok = await confirm(
            'Hand over ' + group.name + '?',
            m.user.displayName + ' becomes the owner. You stay in the group as an admin.',
            { confirmLabel: 'Hand over' }
          );
          if (ok) act.mutate(() => endpoints.handOverGroup(group.id, m.userId));
        }
      });
    }

    /* An admin cannot remove another admin - only the owner can. */
    const mayKick = isOwner || (myRole === 'admin' && m.role === 'member');
    if (mayKick) {
      items.push({
        label: 'Remove from group',
        icon: 'close',
        danger: true,
        onClick: async () => {
          const ok = await confirm(
            'Remove ' + m.user.displayName + '?',
            'They lose access to this conversation. Anyone can add them back later.',
            { confirmLabel: 'Remove', danger: true }
          );
          if (ok) act.mutate(() => endpoints.removeFromGroup(group.id, m.userId));
        }
      });
    }
    return items;
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <section className="overflow-hidden rounded-[var(--radius-l)] border border-line-soft bg-surface-1">
          <div className="flex items-center gap-2 px-3 py-2.5">
            <Icon name="users" size={14} className="flex-none text-mute" />
            <span className="min-w-0 flex-1 truncate text-[11.5px] font-bold tracking-[.06em] uppercase">
              Members
            </span>
            <span className="flex-none rounded-full bg-surface-3 px-1.5 text-[10.5px] font-bold text-dim tabular-nums">
              {group.memberCount}
            </span>
          </div>

          <div className="flex flex-col gap-1 border-t border-line-soft p-2">
            {ordered.map((m) => (
              <MemberRow key={m.userId} m={m} menuFor={menuFor} mine={m.userId === me?.id} />
            ))}
          </div>
        </section>

        <div className="flex flex-col gap-1.5">
          {mayInvite ? (
            <PanelButton icon="addFriend" label="Add someone" onClick={() => setAdding(true)} />
          ) : null}
          {isAdmin ? (
            <PanelButton icon="edit" label="Rename group" onClick={() => setRenaming(true)} />
          ) : null}
          {isOwner ? (
            <PanelButton
              icon={group.openInvites ? 'globe' : 'lock'}
              label={group.openInvites ? 'Anyone can add people' : 'Only admins can add people'}
              note="Tap to change"
              onClick={() => act.mutate(() => endpoints.setGroupInvites(group.id, !group.openInvites))}
            />
          ) : null}
          <PanelButton icon="close" label="Leave group" danger onClick={onLeave} />
        </div>
      </div>

      {adding ? <AddToGroup group={group} onClose={() => { setAdding(false); refresh(); }} /> : null}
      {renaming ? <RenameGroup group={group} onClose={() => { setRenaming(false); refresh(); }} /> : null}
      {dialog}
    </>
  );
}

function MemberRow({ m, menuFor, mine }: {
  m: GroupMember;
  menuFor: (m: GroupMember, anchor: () => HTMLElement | null) => MenuItem[];
  mine: boolean;
}) {
  const row = useRef<HTMLDivElement | null>(null);
  const ctx = useContextMenu(() => menuFor(m, () => row.current));
  return (
    <div
      ref={row}
      {...ctx.props}
      className="flex cursor-pointer items-center gap-2.5 rounded-[var(--radius)] px-1.5 py-1.5 transition-colors hover:bg-surface-2"
    >
      <Avatar user={m.user} size={28} presence />
      <UserName user={m.user} className="min-w-0 flex-1 truncate text-[12.5px] font-semibold" />
      {m.role === 'owner' ? (
        <Icon name="crown" size={12} className="flex-none text-warn" />
      ) : m.role === 'admin' ? (
        <Icon name="shieldNav" size={12} className="flex-none text-accent-hi" />
      ) : null}
      {mine ? <span className="flex-none text-[10px] font-bold text-ghost">YOU</span> : null}
      {ctx.menu}
    </div>
  );
}

function PanelButton({ icon, label, note, danger, onClick }: {
  icon: string;
  label: string;
  note?: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2.5 rounded-[var(--radius-l)] border border-line-soft bg-surface-1 p-2 text-left transition-colors hover:border-line hover:bg-surface-2',
        danger && 'text-bad hover:border-bad'
      )}
    >
      <Icon name={icon} size={15} className="flex-none opacity-80" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold">{label}</span>
        {note ? <span className="block truncate text-[11px] text-mute">{note}</span> : null}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------- the dialogs */

/** Making one, from your friends list. */
export function NewGroup({ onClose, onMade }: { onClose: () => void; onMade: (id: string) => void }) {
  const [picked, setPicked] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const people = (friends.data?.friends ?? []).map((f) => f.user);

  const q = filter.trim().toLowerCase();
  const shown = q
    ? people.filter((u) => (u.username + ' ' + u.displayName).toLowerCase().includes(q))
    : people;

  /* A name nobody typed: the people in it, which is what it is. */
  const suggested = useMemo(() => {
    const names = people.filter((u) => picked.includes(u.username)).map((u) => u.displayName);
    if (!names.length) return '';
    if (names.length <= 3) return names.join(', ');
    return names.slice(0, 2).join(', ') + ' and ' + (names.length - 2) + ' more';
  }, [picked, people]);

  async function make() {
    if (!picked.length) return;
    setBusy(true);
    try {
      const res = await endpoints.createGroup(name.trim() || suggested, picked);
      if (res.refused?.length) {
        toast(res.refused.join(', ') + ' could not be added', 'warn');
      }
      onMade(res.group.id);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New group"
      subtitle="Pick who is in it. You can add more later."
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={!picked.length || busy} onClick={() => void make()}>
            {busy ? 'Creating…' : picked.length ? 'Create with ' + picked.length : 'Create'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={suggested || 'Group name (optional)'}
          maxLength={60}
          className="h-[38px] w-full rounded-[var(--radius)] border border-line bg-surface-inset px-3 text-[13.5px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
        />

        <div className="relative">
          <Icon name="search" size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-mute" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search friends"
            className="h-[38px] w-full rounded-[var(--radius)] border border-line-soft bg-surface-inset pr-3 pl-9 text-[13px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
          />
        </div>

        <div className="max-h-[300px] min-h-[140px] overflow-y-auto rounded-[var(--radius)] border border-line-soft">
          {shown.length ? shown.map((u) => (
            <PickRow
              key={u.id}
              user={u}
              on={picked.includes(u.username)}
              onToggle={() => setPicked((list) =>
                list.includes(u.username) ? list.filter((x) => x !== u.username) : [...list, u.username])}
            />
          )) : (
            <p className="px-3 py-8 text-center text-[12.5px] text-mute">
              {q ? 'Nobody matches that.' : 'Add a friend first.'}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Adding one more, by username, to a group that already exists. */
function AddToGroup({ group, onClose }: { group: Group; onClose: () => void }) {
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState('');
  const toast = useToast();

  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const inGroup = new Set(group.members.map((m) => m.userId));
  const people = (friends.data?.friends ?? []).map((f) => f.user).filter((u) => !inGroup.has(u.id));

  const q = filter.trim().toLowerCase();
  const shown = q
    ? people.filter((u) => (u.username + ' ' + u.displayName).toLowerCase().includes(q))
    : people;

  async function add(u: PublicUser) {
    setBusy(u.id);
    try {
      await endpoints.addToGroup(group.id, u.username);
      toast(u.displayName + ' added', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy('');
    }
  }

  return (
    <Modal title="Add someone" subtitle={group.name} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Icon name="search" size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-mute" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search friends"
            className="h-[38px] w-full rounded-[var(--radius)] border border-line-soft bg-surface-inset pr-3 pl-9 text-[13px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
          />
        </div>

        <div className="max-h-[320px] min-h-[140px] overflow-y-auto rounded-[var(--radius)] border border-line-soft">
          {shown.length ? shown.map((u) => (
            <div key={u.id} className="flex items-center gap-2.5 px-3 py-2">
              <Avatar user={u} size={30} presence />
              <UserName user={u} className="min-w-0 flex-1 truncate text-[13px] font-semibold" />
              <Button kind="ghost" disabled={!!busy} onClick={() => void add(u)}>
                {busy === u.id ? 'Adding…' : 'Add'}
              </Button>
            </div>
          )) : (
            <p className="px-3 py-8 text-center text-[12.5px] text-mute">
              {q ? 'Nobody matches that.' : 'Everyone you know is already here.'}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function RenameGroup({ group, onClose }: { group: Group; onClose: () => void }) {
  const [name, setName] = useState(group.name);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    const next = name.trim();
    if (!next || next === group.name) { onClose(); return; }
    setBusy(true);
    try {
      await endpoints.renameGroup(group.id, next);
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Rename group"
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>Save</Button>
        </>
      }
    >
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
        maxLength={60}
        className="h-[40px] w-full rounded-[var(--radius)] border border-line bg-surface-inset px-3 text-[14px] outline-none transition-colors focus:border-accent"
      />
    </Modal>
  );
}

function PickRow({ user, on, onToggle }: { user: PublicUser; on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
        on ? 'bg-accent-soft' : 'hover:bg-surface-2'
      )}
    >
      <Avatar user={user} size={30} presence />
      <UserName user={user} className="min-w-0 flex-1 truncate text-[13px] font-semibold" />
      <span
        className={cn(
          'grid h-[18px] w-[18px] flex-none place-items-center rounded-[calc(6px*var(--radius-mul,1))] border transition-colors',
          on ? 'border-accent bg-accent text-accent-fg' : 'border-line'
        )}
      >
        {on ? <Icon name="check" size={12} stroke={3} /> : null}
      </span>
    </button>
  );
}

/** Leaving, with the owner warned about what it means. */
export function useLeaveGroup() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const me = useSession((s) => s.me);

  async function leave(group: Group): Promise<boolean> {
    const owner = group.ownerId === me?.id;
    const last = group.memberCount <= 1;

    const ok = await confirm(
      'Leave ' + group.name + '?',
      last
        ? 'You are the last one here, so the group goes with you.'
        : owner
          ? 'You own this group. The longest-standing member takes it over.'
          : 'You will stop receiving messages. Anyone still in it can add you back.',
      { confirmLabel: 'Leave', danger: true }
    );
    if (!ok || !me) return false;

    try {
      await api.del('/api/groups/' + group.id + '/members/' + me.id);
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
      return true;
    } catch (err) {
      toast((err as Error).message, 'bad');
      return false;
    }
  }

  return { leave, dialog };
}

export function GroupHeader({ group, onTogglePanel, panelOpen, onBack, narrow }: {
  group: Group;
  onTogglePanel: () => void;
  panelOpen: boolean;
  onBack?: () => void;
  narrow: boolean;
}) {
  return (
    <header className="flex flex-none items-center gap-3 border-b border-line-soft px-4 py-2.5">
      {narrow && onBack ? (
        <IconButton label="Back" onClick={onBack}>
          <Icon name="chevronR" size={18} className="-scale-x-100" />
        </IconButton>
      ) : null}
      <GroupAvatar group={group} size={34} />
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-[14.5px] font-bold">{group.name}</h2>
        <p className="truncate text-[12px] text-mute">
          {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
        </p>
      </div>
      <ChatTools channelId={'gc:' + group.id} name={group.name} />
      <CallButton room={groupRoom(group.id)} label={'Call ' + group.name} />
      <IconButton label={panelOpen ? 'Hide members' : 'Show members'} onClick={onTogglePanel}>
        <Icon name="users" size={17} />
      </IconButton>
    </header>
  );
}
